import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw, ShieldAlert, ShieldCheck, UserX } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString('fr-FR');
};

const riskMeta = (level) => {
  const normalized = String(level || '').toLowerCase();
  if (normalized === 'high') return { label: 'Eleve', className: 'bg-red-100 text-red-700' };
  if (normalized === 'medium') return { label: 'Moyen', className: 'bg-amber-100 text-amber-700' };
  return { label: 'Faible', className: 'bg-emerald-100 text-emerald-700' };
};

const SecurityFraudMonitoring = () => {
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingKey, setSubmittingKey] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [overviewResponse, eventsResponse] = await Promise.all([
        axios.get(`${API}/admin/security/overview`),
        axios.get(`${API}/admin/security/events`, { params: { limit: 50 } }),
      ]);
      setOverview(overviewResponse.data || null);
      setEvents(eventsResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger le monitoring securite.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const reviewEvent = async (eventId) => {
    const requestKey = `review:${eventId}`;
    setSubmittingKey(requestKey);
    try {
      await axios.post(`${API}/admin/security/events/${eventId}/review`, {});
      toast.success('Evenement marque comme revise.');
      await loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de marquer cet evenement.');
    } finally {
      setSubmittingKey('');
    }
  };

  const clearFlags = async (userId) => {
    const requestKey = `clear:${userId}`;
    setSubmittingKey(requestKey);
    try {
      await axios.post(`${API}/admin/security/users/${userId}/clear-flags`, {});
      toast.success('Flags de securite reinitialises.');
      await loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de reinitialiser les flags.');
    } finally {
      setSubmittingKey('');
    }
  };

  const suspendUser = async (userId) => {
    const requestKey = `suspend:${userId}`;
    setSubmittingKey(requestKey);
    try {
      await axios.patch(`${API}/admin/users/${userId}/toggle-active`, null, { params: { is_active: false } });
      toast.success('Compte suspendu.');
      await loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de suspendre ce compte.');
    } finally {
      setSubmittingKey('');
    }
  };

  const summary = overview?.summary || {};
  const suspectUsers = overview?.suspect_users || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Security & Fraud Monitoring</h2>
          <p className="text-sm text-gray-500">
            Surveillez les abus OTP, promo, parrainage, paiements et tentatives de bypass.
          </p>
        </div>
        <Button variant="outline" onClick={loadAll} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Anomalies ouvertes</CardTitle>
            <CardDescription>Incidents encore a revoir</CardDescription>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">{Number(summary.open_events || 0).toLocaleString('fr-FR')}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Utilisateurs flagges</CardTitle>
            <CardDescription>Flags actifs ou score de risque > 0</CardDescription>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">{Number(summary.flagged_users || 0).toLocaleString('fr-FR')}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Risque eleve</CardTitle>
            <CardDescription>Comptes a examiner rapidement</CardDescription>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">{Number(summary.high_risk_users || 0).toLocaleString('fr-FR')}</CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Utilisateurs suspects</CardTitle>
          <CardDescription>Score global, flags actifs et actions rapides.</CardDescription>
        </CardHeader>
        <CardContent>
          {suspectUsers.length === 0 ? (
            <div className="py-6 text-sm text-gray-500">Aucun utilisateur suspect pour le moment.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Risque</TableHead>
                  <TableHead>Flags</TableHead>
                  <TableHead>Dernier signal</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suspectUsers.map((user) => {
                  const meta = riskMeta(user?.risk_level);
                  const flags = user?.fraud_flags || [];
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{user.full_name || user.email || user.id}</div>
                          <div className="text-gray-500">{user.email || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={meta.className}>{meta.label}</Badge>
                          <span>{Number(user.risk_score || 0).toLocaleString('fr-FR')}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">{flags.length ? flags.join(', ') : '-'}</TableCell>
                      <TableCell>{formatDateTime(user.last_security_event_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={submittingKey === `clear:${user.id}`}
                            onClick={() => clearFlags(user.id)}
                          >
                            <ShieldCheck className="mr-1 h-4 w-4" />
                            Debloquer
                          </Button>
                          {user?.is_active !== false && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={submittingKey === `suspend:${user.id}`}
                              onClick={() => suspendUser(user.id)}
                            >
                              <UserX className="mr-1 h-4 w-4" />
                              Suspendre
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Evenements recents</CardTitle>
          <CardDescription>Journal des OTP abusifs, promos suspects, paiements anormaux et bypass livres.</CardDescription>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="py-6 text-sm text-gray-500">Aucun evenement securite recent.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Categorie</TableHead>
                  <TableHead>Evenement</TableHead>
                  <TableHead>Niveau</TableHead>
                  <TableHead>Contexte</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => {
                  const meta = riskMeta(event?.risk_level || event?.severity);
                  const isReviewed = String(event?.status || '').toLowerCase() === 'reviewed';
                  return (
                    <TableRow key={event.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ShieldAlert className="h-4 w-4 text-red-600" />
                          <span className="text-sm">{event.category}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{event.action}</div>
                          <div className="text-gray-500">{event.resource_type || '-'} {event.resource_id || ''}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={meta.className}>{meta.label}</Badge>
                          <span>{Number(event.risk_score || 0).toLocaleString('fr-FR')}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-sm text-sm text-gray-600">
                        {event?.context?.reason || event?.context?.signals?.join(', ') || '-'}
                      </TableCell>
                      <TableCell>{formatDateTime(event.created_at)}</TableCell>
                      <TableCell>
                        {isReviewed ? (
                          <Badge className="bg-slate-100 text-slate-700">Revise</Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={submittingKey === `review:${event.id}`}
                            onClick={() => reviewEvent(event.id)}
                          >
                            <AlertTriangle className="mr-1 h-4 w-4" />
                            Marquer revise
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityFraudMonitoring;
